/**
 * Structured data (JSON-LD) and document head for the public groups discovery list route.
 * Breadcrumb labels in JSON-LD use English (common practice); UI meta strings use i18n.
 */

import { env } from '@web/src/lib/env.client'
import i18n from '@web/src/lib/intl/i18n'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { absoluteUrlForPath, baseUrl } from '@web/src/lib/utilsClient'

const LIST_PATH = '/group/list'

export type GroupListSeoItem = {
  name: string
  path: string
  imageUrl?: string | null
}

export type GroupListSeoLoaderData = {
  seoList: {
    items: GroupListSeoItem[]
    totalCount: number
  }
}

type GroupListSearchForSeo = {
  name?: string
  tags?: string
  excludeTags?: string
  sortBy?: string
  myGroups?: boolean
}

/** True when URL search filters narrow the catalog (not the “my groups” tab). */
function hasActiveGroupListFilters(search: GroupListSearchForSeo): boolean {
  if (search.name?.trim()) return true
  if (search.tags?.trim()) return true
  if (search.excludeTags?.trim()) return true
  return Boolean(search.sortBy)
}

function buildGroupListBreadcrumbJsonLd(input: { myGroups: boolean }): Record<string, unknown> {
  const secondName = input.myGroups ? 'My Groups' : 'Discover Groups'
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrlForPath('/') },
      { '@type': 'ListItem', position: 2, name: secondName, item: absoluteUrlForPath(LIST_PATH) },
    ],
  }
}

function buildGroupListItemListJsonLd(input: {
  hasFilters: boolean
  items: GroupListSeoItem[]
  totalCount: number
}): Record<string, unknown> {
  const tr = i18n.t.bind(i18n)
  const base: Record<string, unknown> = {
    '@type': 'ItemList',
    numberOfItems: input.totalCount,
  }

  if (input.hasFilters) {
    base.name = tr('Filtered group search results')
  }

  if (input.items.length > 0) {
    base.itemListElement = input.items.map((row, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: row.name,
      item: absoluteUrlForPath(row.path),
      ...(row.imageUrl ? { image: row.imageUrl } : {}),
    }))
  }

  return base
}

export function groupListPageHeadFromLoaderData(
  loaderData: GroupListSeoLoaderData | null | undefined,
  search: GroupListSearchForSeo
) {
  const tr = i18n.t.bind(i18n)
  const myGroups = search.myGroups === true
  const pageTitle = myGroups ? tr('My Groups') : tr('Discover Groups')
  const description = myGroups
    ? tr(
        'Your groups in one place on SpicyFriends: jump back into discussions, events, and updates for every lifestyle and swinger community you already belong to.'
      )
    : tr(
        'Discover lifestyle and swinger groups on SpicyFriends. Search by name, use tags to narrow results, and find communities that match your interests and region.'
      )

  const canonicalUrl = `${baseUrl()}${LIST_PATH}`
  const hasFilters = hasActiveGroupListFilters(search)

  const items = loaderData?.seoList.items ?? []
  const totalCount = loaderData?.seoList.totalCount ?? 0

  const searchResultsPage: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'SearchResultsPage',
    url: canonicalUrl,
    name: pageTitle,
    description,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl()}${LIST_PATH}?name={query}`,
      },
      queryInput: 'required name=query',
    },
    mainEntity: buildGroupListItemListJsonLd({ hasFilters, items, totalCount }),
  }

  const jsonLd: Record<string, unknown>[] = [searchResultsPage, buildGroupListBreadcrumbJsonLd({ myGroups })]

  const ogImage = items[0]?.imageUrl

  return seo({
    title: `${pageTitle} | ${env.VITE_APP_NAME}`,
    description,
    url: canonicalUrl,
    ...(ogImage ? { image: ogImage } : { image: '/logo-og-image.webp' }),
    robots: robotsMetaForEnvironment({
      production: 'index, follow',
      staging: 'noindex,nofollow',
    }),
    jsonLd,
  })
}
